
import React, { useState } from 'react';
import { X, Edit2 } from 'lucide-react';

const AdminSignoffPage = ({ templates, onTemplateUpdate, dropdownOptions }) => {

    const availableMessages = {
        sales: dropdownOptions?.salesMessageNames || [],
        service: dropdownOptions?.serviceMessageNames || []
    };

    const [fieldConfig, setFieldConfig] = useState({
        sales: [
            { id: 'email', name: 'Email Address', type: 'email', required: true },
            { id: 'name', name: 'Name/Job Title', type: 'text', required: true },
            { id: 'signedOff', name: 'Signed Off', type: 'checkbox', required: false }
        ],
        service: [
            { id: 'email', name: 'Email Address', type: 'email', required: true },
            { id: 'name', name: 'Name/Job Title', type: 'text', required: true },
            { id: 'signedOff', name: 'Signed Off', type: 'checkbox', required: false }
        ]
    });
    const [isFieldConfigModalOpen, setIsFieldConfigModalOpen] = useState(false);
    const [configModalType, setConfigModalType] = useState('sales');
    const [editingField, setEditingField] = useState(null);





    const handleFieldAdd = (type, field) => {
        const newConfig = { ...fieldConfig };
        newConfig[type].push(field);
        setFieldConfig(newConfig);
    };

    const handleFieldRemove = (type, fieldId) => {
        const newConfig = { ...fieldConfig };
        newConfig[type] = newConfig[type].filter(field => field.id !== fieldId);
        setFieldConfig(newConfig);

        // Since this is now a global template configuration,
        // we don't need to update location-specific data
    };

    const handleFieldEdit = (type, field) => {
        const newConfig = { ...fieldConfig };
        const fieldIndex = newConfig[type].findIndex(f => f.id === field.id);
        if (fieldIndex !== -1) {
            newConfig[type][fieldIndex] = field;
            setFieldConfig(newConfig);
        }
        setEditingField(null);
    };

    const startEditField = (field) => {
        setEditingField({ ...field });
    };

    const cancelEditField = () => {
        setEditingField(null);
    };


    const openFieldConfigModal = (type) => {
        setConfigModalType(type);
        setIsFieldConfigModalOpen(true);
    };


    const renderMessageTypeConfig = (type) => {
        const allMessageTypes = availableMessages[type];

        return (
        <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold capitalize">{type} Sign Off Template Configuration</h3>
                <button
                    onClick={() => openFieldConfigModal(type)}
                    className="px-4 py-2 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600"
                >
                    Configure Fields
                </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
                Configure the sign-off fields for each {type} message type. These settings will be applied globally across all locations.
            </p>

            <div className="space-y-4">
                {allMessageTypes.map(msgType => (
                    <div key={msgType} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-lg">{msgType}</h4>
                            <div className="text-sm text-gray-500">
                                {fieldConfig[type].length} fields configured
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {fieldConfig[type].map(field => (
                                <div key={field.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded border">
                                    <div className="flex-1">
                                        <div className="font-medium text-sm">{field.name}</div>
                                        <div className="text-xs text-gray-500">
                                            {field.type} {field.required && '(Required)'}
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        {field.type === 'email' ? '📧' :
                                         field.type === 'checkbox' ? '☑️' :
                                         field.type === 'text' ? '📝' : '📄'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                {allMessageTypes.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        No {type} message types configured. Add message types to get started.
                    </div>
                )}
            </div>
        </div>
        );
    };

    return (
        <div>
            <h2 className="text-2xl font-bold mb-6">Master Sign Off Template</h2>
            <div className="mb-4 text-sm text-gray-600">
                Configure sign-off fields for each message type. These settings apply globally across all locations.
                Currently managing {availableMessages.sales.length + availableMessages.service.length} total message types.
            </div>
            <div className="space-y-8">
                {renderMessageTypeConfig('sales')}
                {renderMessageTypeConfig('service')}
            </div>



            {isFieldConfigModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
                        <h3 className="text-lg font-bold mb-4">Configure {configModalType === 'sales' ? 'Sales' : 'Service'} Fields</h3>

                        <div className="space-y-3 mb-4">
                            {fieldConfig[configModalType].map((field) => (
                                <div key={field.id} className="flex items-center gap-3 p-3 border rounded">
                                    {editingField && editingField.id === field.id ? (
                                        <div className="flex-1 grid grid-cols-2 gap-3">
                                            <div>
                                                <input
                                                    type="text"
                                                    value={editingField.name}
                                                    onChange={(e) => setEditingField({...editingField, name: e.target.value})}
                                                    className="w-full px-2 py-1 border rounded text-sm"
                                                    placeholder="Field Name"
                                                />
                                            </div>
                                            <div>
                                                <select
                                                    value={editingField.type}
                                                    onChange={(e) => setEditingField({...editingField, type: e.target.value})}
                                                    className="w-full px-2 py-1 border rounded text-sm"
                                                >
                                                    <option value="text">Text</option>
                                                    <option value="email">Email</option>
                                                    <option value="tel">Phone</option>
                                                    <option value="url">URL</option>
                                                    <option value="number">Number</option>
                                                    <option value="date">Date</option>
                                                    <option value="checkbox">Checkbox</option>
                                                </select>
                                            </div>
                                            <div className="col-span-2 flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={editingField.required}
                                                    onChange={(e) => setEditingField({...editingField, required: e.target.checked})}
                                                    className="w-4 h-4"
                                                />
                                                <span className="text-sm">Required field</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex-1">
                                            <div className="font-medium">{field.name}</div>
                                            <div className="text-sm text-gray-500">Type: {field.type} {field.required && '(Required)'}</div>
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        {editingField && editingField.id === field.id ? (
                                            <>
                                                <button
                                                    onClick={() => handleFieldEdit(configModalType, editingField)}
                                                    className="text-green-500 hover:text-green-700"
                                                    title="Save"
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    onClick={cancelEditField}
                                                    className="text-gray-500 hover:text-gray-700"
                                                    title="Cancel"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => startEditField(field)}
                                                    className="text-blue-500 hover:text-blue-700"
                                                    title="Edit"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleFieldRemove(configModalType, field.id)}
                                                    className="text-red-500 hover:text-red-700"
                                                    disabled={fieldConfig[configModalType].length <= 1}
                                                    title="Delete"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            const newField = {
                                id: Date.now().toString(),
                                name: formData.get('fieldName'),
                                type: formData.get('fieldType'),
                                required: formData.get('required') === 'on'
                            };
                            handleFieldAdd(configModalType, newField);
                            e.target.reset();
                        }}>
                            <div className="border-t pt-4">
                                <h4 className="font-medium mb-3">Add New Field</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Field Name</label>
                                        <input
                                            type="text"
                                            name="fieldName"
                                            placeholder="e.g., Phone Number"
                                            className="w-full px-3 py-2 border rounded-lg"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Field Type</label>
                                        <select name="fieldType" className="w-full px-3 py-2 border rounded-lg" required>
                                            <option value="text">Text</option>
                                            <option value="email">Email</option>
                                            <option value="tel">Phone</option>
                                            <option value="url">URL</option>
                                            <option value="number">Number</option>
                                            <option value="date">Date</option>
                                            <option value="checkbox">Checkbox</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    <input type="checkbox" name="required" className="w-4 h-4" />
                                    <span className="text-sm">Required field</span>
                                </div>
                                <div className="flex justify-end gap-3 mt-4">
                                    <button type="submit" className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg">
                                        Add Field
                                    </button>
                                </div>
                            </div>
                        </form>

                        <div className="flex justify-end mt-4">
                            <button
                                onClick={() => setIsFieldConfigModalOpen(false)}
                                className="px-4 py-2 text-sm rounded-lg border"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSignoffPage;